import { IAuthenticatedAccount, IRegion, IUser } from '@alberta/konexi-shared';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthActions, AuthService as MobileOAuthService } from 'ionic-appauth';
import { omit } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { AuthServiceWrapper } from 'src/app/auth/factories';
import { IAuthEventData } from 'src/app/common/contracts/authentication/auth-event-data';
// tslint:disable-next-line: max-line-length
import { Deferred } from 'src/app/common/deferred/deferred';
import { isTokenAboutToExpire } from 'src/app/common/jwt/verifyJWT';

import hydratedOrganization from '@alberta/konexi-shared/dist/auth/hydrated-organization';
import { environment } from 'src/environments/environment.default';
import makeDebug from '../../../makeDebug';
import { ConnectionStateService } from './connection-state.service';
import { IConnectionStateService } from './contracts/sync/connection-state-service';
import { IFeathersAppProvider } from './contracts/sync/feathers-app-provider';
import { EnvironmentService } from './environment/environment.service';
import { FeathersService } from './feathers.service';
import { SessionVaultService } from './session-vault/session-vault.service';
import { TrackerService } from './tracker.service';

const debug = makeDebug('auth:auth');

/**
 * The token validity buffer is the time in seconds that is added to the token expiration time
 * to ensure that the token is still valid when it is used.
 * The formula is:  return now < this.issuedAt + this.expiresIn + buffer;
 * So if you enter -2*60 the token is valid if it has at least 2 minutes left.
 */
export const TOKEN_VALIDITY_BUFFER_IN_SECONDS = -2 * 60;

//#region INTERFACES

/**
 * Authentication result from Feathers backend after successful JWT authentication.
 * Contains the access token, JWT payload, and hydrated user account.
 */
export interface AlbertaAuthenticationResult {
  authentication?: {
    strategy?: string;
    accessToken?: string;
    payload?: {
      aud?: string;
      exp?: number;
      iat?: number;
      iss?: string;
      sub?: string;
      jti?: string;
      authenticationType?: string;
      tty?: string;
      email?: string;
      email_verified?: boolean;
      roles?: string[];
      auth_time?: number;
      scope?: string;
      applicationId?: string;
      tid?: string;
      sid?: string;
      gty?: string[];
    };
  };
  accessToken?: string;
  user?: IAuthenticatedAccount;
}

export interface IAuthService {
  authenticate(isOnline: boolean): Promise<boolean>;
  getAccessToken(): Promise<string | null>;
  refreshTokenMobile(): Promise<boolean>;
  configure(): Promise<void>;
  login(email: string): Promise<void>;
  logout(): Promise<void>;
  getCurrentUserName(): Promise<string | null>;
  getCurrentUserICSToken(): Promise<string | null>;
  setAuthenticated(isAuthenticated: boolean, isOnline?: boolean): Promise<void>;
  getCurrentUserId(): Promise<string>;
  resetRights(rights: any): Promise<void>;
  resetCurrentUser(user: IUser, regions: IRegion[]): Promise<void>;
  getUserPermission(domain: string): Promise<number>;
  userHasPermission(domain: string, permission: number): Promise<boolean>;
  getCurrentUserGroups(): Promise<string[] | null>;
  getCurrentUserRegions(): Promise<IRegion[] | null>;
  getCurrentUserTenantName(): Promise<string | null>;
  getCurrentUserOrganization(): Promise<hydratedOrganization | null>;
  isTokenValid(): Promise<boolean>;
}

//#endregion

@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  //#region PRIVATE PROPERTIES
  private _tokenExpiredSubscription: Subscription;
  // FUTURE TODO: We might delete _init and use accountReady instead.
  // They seem redudant but might be needed for sync integrity.
  // If you come here and feel brave. Do it and test it.
  private _init = new Deferred<void>();
  private _authenticationEventSubject = new BehaviorSubject<IAuthEventData>({
    isAuthenticated: false,
  });
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private _mobileAccessToken: Deferred<string>;
  private _account$ = new BehaviorSubject<IAuthenticatedAccount | null>(null);
  private _accountReady = new Deferred<IAuthenticatedAccount>();

  private get isCordova(): boolean {
    return this._platform.is('hybrid');
  }
  private _refreshTokenPromise: Promise<boolean> | null = null;
  //#endregion

  //#region PUBLIC PROPERTIES
  public init = this._init.promise;
  public redirectUrl: string;
  public authenticatedEventPublisher = this._authenticationEventSubject.asObservable();

  /**
   * Promise that resolves when the account is loaded.
   */
  public readonly accountReady = this._accountReady.promise;

  /**
   * Synchronous access to the current account. Returns null if not authenticated.
   */
  public async getAccount(): Promise<IAuthenticatedAccount | null> {
    await this.accountReady;
    if (!this._account$.value) {
      const errorMessage = 'Account was accesed. But was not defined in authService';
      window.logger.error(errorMessage, new Error(errorMessage));
    }
    return this._account$.value;
  }

  /**
   * Returns whether the user is currently authenticated.
   */
  public get isAuthenticated(): boolean {
    return this._isAuthenticated$.value;
  }

  /**
   * Current user's full name or null if not authenticated.
   */
  public async getUserFullName(): Promise<string> {
    const account = await this.getAccount();
    return `${account.firstName} ${account.lastName}`;
  }

  /**
   * Current user's email or null if not authenticated.
   */
  public async getUserEmail(): Promise<string> {
    const account = await this.getAccount();
    return account?.email;
  }

  public async isTokenValid(): Promise<boolean> {
    if (!this.isCordova) {
      return this._WebOauthService.hasValidAccessToken();
    }
    try {
      const tokenResponse = await this._mobileOAuthService.getValidToken();
      return tokenResponse.isValid(TOKEN_VALIDITY_BUFFER_IN_SECONDS);
    } catch {
      return false;
    }
  }

  /**
   * Current user's regions. Returns empty array if not authenticated.
   */
  public async getUserRegions(): Promise<IRegion[]> {
    const account = await this.getAccount();
    return account?.authorization?.regions;
  }

  public async getCalendarConnector(): Promise<string> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.calendarConnector;
  }

  /**
   * Current user's groups. Returns empty array if not authenticated.
   */
  public async getUserGroups(): Promise<string[]> {
    const account = await this.getAccount();
    return account?.groups;
  }

  /**
   * Users with the debug flag will send verbose debug information.
   * This flag should only be used temporarely and should be set to false asap.
   */
  public async isDebugUser(): Promise<boolean> {
    const account = await this.getAccount();
    return account.debug === true;
  }

  public async isAgent(): Promise<boolean> {
    const account = await this.getAccount();
    return account.isAgent === true;
  }

  public async setO365Id(o365Id: string): Promise<void> {
    const account = await this.getAccount();
    account.o365Id = o365Id;
    await this.storage.set('currentUser', account);
    this._account$.next(account);
  }

  public async getO365Id(): Promise<string> {
    const account = await this.getAccount();
    return account.o365Id;
  }

  /**
   * Current user's first name.
   */
  public async getUserFirstName(): Promise<string> {
    const account = await this.getAccount();
    return account?.firstName;
  }

  /**
   * Current user's last name.
   */
  public async getUserLastName(): Promise<string> {
    const account = await this.getAccount();
    return account?.lastName;
  }

  /**
   * Current user's mobile phone.
   */
  public async getUserMobilePhone(): Promise<string> {
    const account = await this.getAccount();
    return account?.mobilePhone;
  }

  public async userIsItLabs(): Promise<boolean> {
    const account = await this.getAccount();

    const userIsItLabs =
      environment.validITLabsUser.some(mailOrSuffixToCheck => account.email.includes(mailOrSuffixToCheck)) ||
      (account.canChangeChannel != null && Boolean(account.canChangeChannel));

    return userIsItLabs;
  }

  public async canCreateTenant(): Promise<boolean> {
    const account = await this.getAccount();

    const userIsItLabs = environment.validITLabsUser.some(mailOrSuffixToCheck =>
      account.email.includes(mailOrSuffixToCheck)
    );

    return userIsItLabs;
  }

  //#endregion

  constructor(
    @Inject(ConnectionStateService) private _connectionStateService: IConnectionStateService,
    private storage: Storage,
    @Inject(FeathersService) private _feathersAppProvider: IFeathersAppProvider,
    private _tracker: TrackerService,
    private _platform: Platform,
    private _WebOauthService: OAuthService,
    @Inject(MobileOAuthService) private _mobileOAuthService: AuthServiceWrapper,
    private _ngZone: NgZone,
    private _router: Router,
    private _sessionVault: SessionVaultService,
    private _environmentService: EnvironmentService
  ) {
    this._connectionStateService.connected.pipe(filter(isOnline => !isOnline)).subscribe(() => {
      this._authenticationEventSubject.next({ isAuthenticated: false });
    });
    if (this._platform.is('hybrid') === false) {
      this._WebOauthService.events
        .pipe(
          filter(event => event.type === 'token_received'),
          take(1),
          tap(event => {
            this.redirectUrl = decodeURIComponent(this._WebOauthService.state) || '/dashboard';
            if (this.redirectUrl !== '/dashboard' && this.redirectUrl !== '/login') {
              this._router.navigate([this.redirectUrl]);
            }
          })
        )
        .subscribe();
    }
  }

  //#region AUTHENTICATION METHODS

  /**
   * Authenticate the user.
   * @param isOnline - Whether the user is online or not.
   * @returns A promise that resolves to a boolean indicating whether the authentication was successful.
   * Call configure before authenticate or the token might be set wrong
   */
  public async authenticate(isOnline: boolean): Promise<boolean> {
    debug('authenticate');
    window.logger.addBreadcrumb({
      category: 'auth',
      message: 'call authenticate',
      data: {
        isOnline,
      },
    });
    if (!isOnline) {
      return this.authenticateOffline();
    } else if (isOnline && !this.isCordova) {
      await this._WebOauthService.tryLogin();
    }
    debug('has valid access token', this._WebOauthService.hasValidAccessToken());
    // XXX this could be used to start the login flow to prevent the display of the login page
    // if (!this._oauthService.getRefreshToken) {
    //   this._oauthService.initLoginFlow();
    //   return;
    // }
    const accessToken = await this.getAccessToken();

    if (!accessToken) {
      window.logger.error('no access token', new Error('no access token'));
      // we cannot log into feathers app without an access token
      // Consuming methods need to trigger reset
      return false;
    }

    return this.loginFeathersApp(accessToken);
  }
  /**
   * Get a valid access token.
   * Return null if no valid access token is available.
   * Token on mobile is valid with a time threshold of TOKEN_VALIDITY_BUFFER_IN_SECONDS.
   * Token in web is valid if not expired due to different plugins
   * @returns
   */
  public async getAccessToken(): Promise<string | null> {
    if (this.isCordova) {
      return this.getValidMobileAccessToken();
    }
    return this._WebOauthService.getAccessToken();
  }

  /**
   * Get a valid access token from the mobile oauth service.
   * Return null if no valid access token is available.
   * @returns
   */
  private async getValidMobileAccessToken(): Promise<string | null> {
    try {
      const tokenResponse = await this._mobileOAuthService.getValidToken();

      if (tokenResponse.isValid(TOKEN_VALIDITY_BUFFER_IN_SECONDS)) {
        return tokenResponse.accessToken;
      }
    } catch (error) {
      console.warn('Failed to obtain valid access token', error);
    }
    window.logger.error('no valid access token', new Error('no valid acess token'));
    return null;
  }

  private async loginFeathersApp(accessToken: string): Promise<boolean> {
    debug(`login with access token`, accessToken);

    try {
      const authenticationResult: AlbertaAuthenticationResult = await this.tryAccessTokenOrByRefreshToken(accessToken);
      debug('got auth result', authenticationResult);

      await this.storage.set('currentUser', authenticationResult.user);
      this._account$.next(authenticationResult.user);
      this._accountReady.resolve(authenticationResult.user);

      debug('updated authentication account', authenticationResult.user);
      this.initializeTracker(authenticationResult.user);
      this.observeTokenExpiration(authenticationResult.accessToken);
      return true;
    } catch (error) {
      const isFeathersError = error.type === 'FeathersError';
      const isCriticalError = ['timeout', 'not-authenticated'].includes(error.className) === false;
      if (!isFeathersError || isCriticalError) {
        window.logger.error('AUTHSERVICE AUTHENTICATE', error);
      } else {
        console.error('expected feathers error happened', error);
      }
      if (isCriticalError) {
        window.logger.error('loginFeathersApp failed', new Error(JSON.stringify({ isFeathersError, isCriticalError })));
      } else {
        window.logger.info(`loginFeathersApp failed ${JSON.stringify({ isFeathersError, isCriticalError })}`);
      }
      return false;
    }
  }

  /**
   * Refreshes the access token using the mobile OAuth service.
   * This method is used specifically for mobile applications.
   * In web it returns false
   * @returns A promise that resolves to true if the token was refreshed successfully, false otherwise
   */
  public async refreshTokenMobile(): Promise<boolean> {
    debug('refresh token');

    // Return existing promise if refresh is already in progress
    if (this._refreshTokenPromise) {
      debug('refresh already in progress, returning existing promise');
      return this._refreshTokenPromise;
    }

    if (this.isCordova) {
      const oldAccessToken = await this._mobileAccessToken.promise;

      if (!oldAccessToken || oldAccessToken === 'dummy-accessToken') {
        debug(`refreshToken failed with old accessToken - ${oldAccessToken}`);
        return false;
      }

      this._refreshTokenPromise = new Promise<boolean>(resolve => {
        let refreshTokenObserver: any;
        refreshTokenObserver = this._mobileOAuthService.addActionListener(async action => {
          if (action.action === AuthActions.RefreshSuccess) {
            this._mobileOAuthService.removeActionObserver(refreshTokenObserver);
            debug(`refreshToken succeeded with new accessToken - ${action.tokenResponse.accessToken}`);
            this._refreshTokenPromise = null;
            resolve(true);
          }
          if (action.action === AuthActions.RefreshFailed) {
            this._mobileOAuthService.removeActionObserver(refreshTokenObserver);
            this._refreshTokenPromise = null;
            await this.logout();
            window.location.reload();
          }
        });
      });

      await this._mobileOAuthService.refreshToken();

      return this._refreshTokenPromise;
    }

    return false;
  }

  public async configure(): Promise<void> {
    return this.isCordova ? this.configureMobile() : this.configureBrowser();
  }

  public async login(email: string): Promise<void> {
    debug('login', email);
    if (email) {
      email = email.trim().toLowerCase();
    }

    if (this.isCordova) {
      await this._mobileOAuthService.signIn({ login_hint: email });
    } else {
      // TODO: renenable if code flow works with login_hint
      // PR https://github.com/manfredsteyer/angular-oauth2-oidc/pull/938 is merged
      // this._oauthService.initLoginFlow(this.redirectUrl, email);
      this._WebOauthService.initLoginFlow(this.redirectUrl);
    }
  }

  public async logout(): Promise<void> {
    debug('logout');

    try {
      await navigator?.serviceWorker.getRegistrations().then(async function (registrations) {
        for (const registration of registrations) {
          await registration.unregister();
        }
      });
    } catch (error) {}

    await this._feathersAppProvider.app.authentication.removeAccessToken();

    if (this._connectionStateService.getSocketConnectionState()) {
      try {
        this._tracker.trackLogout();
        await this._feathersAppProvider.app.logout();
      } catch (error) {
        window.logger.error('logout request at backend failed', error);
      }
    }
    await this.storage.remove('currentUser');
    // Reset accountReady for next login - create a new Deferred instance
    this._accountReady = new Deferred<IAuthenticatedAccount>();
    this._account$.next(null);
    this._isAuthenticated$.next(false);

    this._authenticationEventSubject.next({
      isAuthenticated: false,
    });
    this._tracker.clearIdentify();
    if (this._platform.is('hybrid') && this._sessionVault.vaultInitialized) {
      await this._sessionVault.clearVaultAndResetLockMode();
    }

    if (this._connectionStateService.getSocketConnectionState()) {
      this.isCordova ? await this._mobileOAuthService.signOut() : this._WebOauthService.logOut();
    } else if (!this._connectionStateService.getSocketConnectionState() && this.isCordova) {
      await this._mobileOAuthService.removeToken();
      window.location.reload();
    }
  }

  private async tryAccessTokenOrByRefreshToken(accessToken: string): Promise<AlbertaAuthenticationResult> {
    await this._environmentService.ready;
    try {
      return (await this._feathersAppProvider.app.authenticate({
        strategy: 'fusionAuthJwt',
        accessToken,
        applicationId: this._environmentService.authConfig.authCodeFlowConfigWeb.clientId,
      })) as AlbertaAuthenticationResult;
    } catch (error) {
      if (error.code === 401) {
        const newAccessToken = await this.getAccessTokenByRefreshToken();
        return (await this._feathersAppProvider.app.authenticate({
          strategy: 'fusionAuthJwt',
          accessToken: newAccessToken,
          applicationId: this._environmentService.authConfig.authCodeFlowConfigWeb.clientId,
        })) as AlbertaAuthenticationResult;
      }
      window.logger.error('tryAccessTokenOrByRefreshToken failed', error);
      throw error;
    }
  }

  public async setAuthenticated(isAuthenticated: boolean, isOnline?: boolean): Promise<void> {
    this._isAuthenticated$.next(isAuthenticated);
    this._authenticationEventSubject.next({ isAuthenticated, authOnline: isOnline });
    this._init.resolve();
    this._tracker.isAuthOnline = isOnline;
    this._tracker.isAuthenticated = isAuthenticated;
  }

  private async authenticateOffline(): Promise<boolean> {
    const currentUser = await this.storage.get('currentUser');
    if (
      !currentUser ||
      typeof currentUser !== 'object' ||
      !currentUser._id ||
      !currentUser.authorization ||
      !currentUser.authorization.hydratedOrganization
    ) {
      window.logger.error('authenticateOffline failed', new Error('currentUser is not valid'));
      return false;
    }
    try {
      const accessToken = this.isCordova
        ? await this._mobileAccessToken.promise
        : this._WebOauthService.getAccessToken();

      if (accessToken != null && accessToken !== 'dummy-accessToken') {
        this.observeTokenExpiration(accessToken);
        this._account$.next(currentUser);
        this._accountReady.resolve(currentUser);

        this.initializeTracker(currentUser);
      }
    } finally {
      return currentUser != null;
    }
  }

  private async configureBrowser() {
    await this._environmentService.ready;
    debug('configure browser');
    if (!this._WebOauthService.tokenEndpoint) {
      // ! prevent reconfigure!
      this._WebOauthService.configure(this._environmentService.authConfig.authCodeFlowConfigWeb);
      await this._WebOauthService.loadDiscoveryDocument();
      this._WebOauthService.setupAutomaticSilentRefresh();
    }
  }

  private async configureMobile(): Promise<void> {
    this._mobileAccessToken = new Deferred<string>();
    const authConfigured = new Deferred<void>();

    await this._mobileOAuthService.events$
      .pipe(
        filter(
          action =>
            action?.action === AuthActions.LoadTokenFromStorageSuccess ||
            action?.action === AuthActions.LoadTokenFromStorageFailed
        ),
        take(1)
      )
      .subscribe(async action => {
        switch (action.action) {
          case AuthActions.LoadTokenFromStorageSuccess:
            this._mobileAccessToken.resolve(action.tokenResponse.accessToken);
            break;
          case AuthActions.LoadTokenFromStorageFailed:
            let token = await this.getValidMobileAccessToken();
            if (!token) {
              token = 'dummy-accessToken';
            }
            this._mobileAccessToken.resolve(token);
            break;
          default:
            break;
        }

        authConfigured.resolve();
      });

    await this._mobileOAuthService.loadTokenFromStorage();

    return authConfigured.promise;
  }

  private observeTokenExpiration(accessToken) {
    debug('setup token expiration observer');
    if (this._tokenExpiredSubscription && !this._tokenExpiredSubscription.closed) {
      this._tokenExpiredSubscription.unsubscribe();
    }

    if (this.isCordova) {
      this._tokenExpiredSubscription = isTokenAboutToExpire(accessToken, this._ngZone).subscribe({
        complete: async () => {
          if (this._connectionStateService.getSocketConnectionState()) {
            debug('token will expire... refresh');
            await this.refreshTokenMobile();
            debug('accessToken refreshed');
            await this.configure();
            await this.authenticate(this._connectionStateService.isConnected);
            debug('authenticate called with new accessToken');
          }
        },
        error: err => console.error(err),
      });
    } else {
      this._WebOauthService.events
        .pipe(
          filter(e => e.type === 'token_received'),
          take(1)
        )
        .subscribe(async () => {
          await this.authenticate(this._connectionStateService.isConnected);
        });
    }
  }

  private async getAccessTokenByRefreshToken() {
    if (this._platform.is('hybrid')) {
      return new Promise<string>(async resolve => {
        await this.refreshTokenMobile();
        await this.configureMobile();
        const accessToken = await this._mobileAccessToken.promise;

        resolve(accessToken);
      });
    } else {
      const tokenResponse = await this._WebOauthService.refreshToken();
      return tokenResponse.access_token;
    }
  }

  //#endregion

  //#region ACCESS METHODS TO AUTHENTICATED USER OBJECT

  public async getCurrentUserName(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account) {
      const errorMessage = 'CurrentUser not found';
      window.logger.error(errorMessage, new Error(errorMessage));
      return null;
    }
    return account?.name;
  }

  public async getCurrentUserICSToken(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account?.icsToken) {
      const errorMessage = 'CurrentUserICSToken not found';
      window.logger.error(errorMessage, new Error(errorMessage));
      return null;
    }
    return account?.icsToken;
  }

  public async getCurrentUserId(): Promise<string> {
    const account = await this.getAccount();
    return account._id;
  }

  public async canUseAlbertaLight(): Promise<boolean> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.canUseAlbertaLight === true;
  }

  public async getCurrentUserGroups(): Promise<string[] | null> {
    const account = await this.getAccount();
    if (!account?.groups) {
      const errormessage = `{user groups not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.groups;
  }

  public async getCurrentUserRegions(): Promise<IRegion[] | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.regions) {
      const errormessage = `{user regions not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.authorization.regions;
  }

  /**
   * Returns the tenantId of the current user in the organization property.
   * CAREFUL: THIS IS THE NAME OF THE TENANT, NOT THE ID!
   * EXAMPLE: '1TLabs'
   * @returns The tenantId of the current user.
   */
  public async getCurrentUserTenantName(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.hydratedOrganization?.tenantId) {
      window.logger.error('getCurrentUserTenantId failed', new Error('getCurrentUserTenantId failed'));
      return null;
    }
    return account.authorization.hydratedOrganization.tenantId;
  }

  public async getCurrentUserTenantId(): Promise<string | null> {
    const account = await this.getAccount();
    return account?.tenantId;
  }

  public async getCurrentUserOrganization(): Promise<hydratedOrganization | null> {
    const account = await this.getAccount();
    return account.authorization?.hydratedOrganization;
  }

  public async resetRights(rights): Promise<void> {
    if (!rights) {
      return;
    }
    const account = await this.getAccount();
    if (!account) {
      return;
    }

    account.authorization.rightSets = rights;
    this._account$.next(account);
    await this.storage.set('currentUser', account);
  }

  public async setRegions(regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account) {
      return;
    }

    account.authorization.regions = regions;
    this._account$.next(account);
    await this.storage.set('currentUser', account);
  }

  public async resetCurrentUser(user: IUser, regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account) {
      return;
    }
    const updatedAccount = { ...account, ...omit(user, ['organization']) };
    updatedAccount.authorization.regions = regions;
    this._account$.next(updatedAccount);
    await this.storage.set('currentUser', updatedAccount);
  }

  public async getUserPermission(domain: string): Promise<number> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return 0;
    }
    const rightSet = account.authorization.rightSets.find(auth => auth.domain === domain);
    return rightSet ? rightSet.permission : 0;
  }

  public async userHasPermission(domain: string, permission: number): Promise<boolean> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return false;
    }

    return account.authorization.rightSets.some(
      rightSet => rightSet.permission >= permission && rightSet.domain === domain
    );
  }
  //#endregion

  //#region HELPER METHODS

  private initializeTracker(account: any) {
    try {
      this._tracker.identify(account._id, account.email, account.organization);
    } catch (error) {
      window.logger.error('tracker initialization failed', error);
    }
  }
  //#endregion
}
